// Import TinyMCE and required files
import tinymce from 'tinymce';

import 'tinymce/icons/default';
import 'tinymce/themes/silver';

// Import plugins as needed
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/anchor';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/code';
import 'tinymce/plugins/emoticons';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/help';
import 'tinymce/plugins/hr';
import 'tinymce/plugins/image';
import 'tinymce/plugins/imagetools';
import 'tinymce/plugins/insertdatetime';
import 'tinymce/plugins/link';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/nonbreaking';
import 'tinymce/plugins/paste';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/print';
import 'tinymce/plugins/quickbars';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/table';
import 'tinymce/plugins/textpattern';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/visualchars';
import 'tinymce/plugins/wordcount';

import filePickerCallback from '../../tinymce_file_upload_handlers/file_uploads';
import imageUploadHandler from '../../tinymce_file_upload_handlers/image_uploads';

const buildSubmenuItems = (variableList, editor) => {
  const items = [];

  variableList.forEach((currentElement) => {
    items.push({
      type: 'menuitem',
      text: currentElement.label,
      onAction() {
        editor.insertContent(`{{ ${currentElement.variable_name} }}`);
      },
    });
  });

  return items;
};

const buildNestedItem = (parentLabel, submenuItems) => ({
  type: 'nestedmenuitem',
  text: parentLabel,
  getSubmenuItems() {
    return submenuItems;
  },
});

const buildVariableItems = (variables, editor) => {
  const items = [];

  Object.entries(variables).forEach(([parentLabel, variableList]) => {
    const submenuItems = buildSubmenuItems(variableList, editor);
    const nestedItem = buildNestedItem(parentLabel, submenuItems);

    items.push(nestedItem);
  });

  return items;
};

// Toggle Edit or Read-Only mode based on the `disabled` attribute
const setEditMode = (editor, disabled) => {
  if (disabled === 'true') {
    editor.mode.set('readonly');
  } else {
    editor.mode.set('design');
  }
};

const initEditor = (lv, variables) => {
  const emojis = import('tinymce/plugins/emoticons/js/emojis');

  tinymce.init({
    target: lv.querySelector('textarea'),
    content_css: '/vendor/tinymce/content/default/content.css',
    content_style: '.mce-content-readonly { background-color: whitesmoke; }',
    contextmenu: 'image link table',
    deprecation_warnings: false,
    emoticons_database_url: emojis,
    file_picker_callback: filePickerCallback,
    file_picker_types: 'file',
    help_tabs: ['shortcuts', 'keyboardnav'],
    images_upload_handler: imageUploadHandler,
    menu: {
      insert: {
        title: 'Insert',
        items: 'image insert_file link media inserttable | charmap emoticons hr | insertdatetime',
      },
    },
    min_height: 500,
    paste_as_text: true,
    paste_data_images: false,
    paste_merge_formats: true,
    plugins: [
      'advlist anchor autolink charmap code emoticons fullscreen help hr image imagetools insertdatetime link lists paste preview print quickbars searchreplace table textpattern visualblocks visualchars wordcount',
    ],
    quickbars_insert_toolbar: false,
    relative_urls: false,
    removed_menuitems: 'newdocument, restoredraft, visualaid',
    skin_url: '/vendor/tinymce/skins/ui/oxide',
    toolbar1:
      'variables styleselect fontselect fontsizeselect | bold italic strikethrough forecolor backcolor',
    toolbar2:
      'alignleft aligncenter alignright alignjustify | link table | bullist numlist outdent indent | emoticons | removeformat fullscreen',

    setup(editor) {
      editor.ui.registry.addMenuItem('insert_file', {
        text: 'File...',
        icon: 'upload',
        onAction: () => {
          // Open Link dialog
          editor.execCommand('mceLink');
        },
      });
      editor.ui.registry.addMenuButton('variables', {
        text: 'Variables',
        fetch(callback) {
          const items = buildVariableItems(variables, editor);
          callback(items);
        },
      });
      editor.on('init', () => {
        const { disabled } = lv.dataset;
        setEditMode(editor, disabled);
      });
      editor.on('OpenWindow', (eventDetails) => {
        // Keep track of the most recently opened window/dialog so we can perform actions on it
        // such as blocking the UI when uploading files.
        window.tinymceTopLevelWindow = eventDetails.dialog;
      });
      editor.on('ExecCommand', (e) => {
        // If the command refers to the Link dialog opening
        if (e.command === 'mceLink') {
          // Wait for DOM to update
          setTimeout(() => {
            // Update Dialog UI
            document.querySelector('.tox-dialog .tox-dialog__title').innerHTML = 'Insert File';
            document.querySelector('.tox-dialog .tox-label').innerHTML = 'Uploaded File URL';
            const uploadButton = document.querySelector('.tox-dialog .tox-browse-url');
            uploadButton.innerHTML = `Upload File&nbsp;&nbsp;${uploadButton.innerHTML}`;
            uploadButton.querySelector('svg').style.display = 'inline-block';

            // Click the Browse button to open the file upload dialog
            uploadButton.click();
          }, 10);
        }
      });
    },
  });
};

export default {
  GroupEmailerWysiwygEditor: {
    mounted() {
      window.addEventListener('phx:compose', (e) => {
        // Remove any previous instances of TinyMCE, just in case
        tinymce.remove();

        // Save the file upload request URL so we can use it in the upload handlers
        window.fileUploadRequestUrl = e.detail.file_upload_request_url;

        // Initialize TinyMCE with Variables
        initEditor(this.el, e.detail.variables);

        // Dispatch "wysiwyg-change" LiveView event with the editor content
        const dispatchEditorChangeEvent = (editor) => {
          const content = editor.getContent();
          this.pushEventTo(this.el, 'wysiwyg-change', {
            content,
          });
        };

        // Trigger "wysiwyg-change" LiveView event when the editor content changes.
        // Debounce the event so we don't send too many events to the LiveView process.
        let editorChangeHandler;
        tinymce.activeEditor.on('blur change input keyup', () => {
          clearTimeout(editorChangeHandler);
          editorChangeHandler = setTimeout(() => {
            dispatchEditorChangeEvent(tinymce.activeEditor);
          }, 250);
        });
      });

      window.addEventListener('phx:wysiwyg-update-content', (e) => {
        // Update the editor content when LiveView sends a "wysiwyg-update-content" event.
        // This happens when the draft editor is making updates to the content.
        const { content } = e.detail;
        if (content) {
          tinymce.activeEditor.setContent(content);
        }
      });
    },
    updated() {
      const { disabled } = this.el.dataset;
      setEditMode(tinymce.activeEditor, disabled);
    },
    destroyed() {
      // Remove TinyMCE instance when we leave so it can be re-initialized when we visit again.
      // If we don't do this, TinyMCE won't show if we open another Page for editing since
      // a previous instance would already exist.
      tinymce.remove();
    },
  },
};
